import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { AreaChart } from '../../../organisms/AreaChart/AreaChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type CommunityActivityTimelineView = {
  /** `Comments per post`, `Posts vs previous period`, `Comments vs previous period`. */
  kpis: KPIMetric[];
  /** One row per week, keys `posts` and `comments`. */
  rows: ChartRow[];
  xKey: string;
  /** `Posts` (chart-1) and `Comments` (chart-2). */
  series: ChartSeries[];
  /** Whether any community row is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type CommunityActivityTimelineCardProps = WidgetBlockProps & {
  view?: CommunityActivityTimelineView;
  /** Opens a week's posts and comments (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:19575` — posts and the replies to them, week by week.
 *
 * Weekly rather than letting the range decide, which would be daily for the
 * default 70-day window: the window holds about 51 posts and 107 comments inside
 * communities, so daily buckets put 0.7 posts on each and the series is a row of
 * ones and zeroes. Weekly gives ten buckets of about 5 and 11.
 *
 * Full width by default — the module draws it at 12 tracks.
 */
export function CommunityActivityTimelineCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: CommunityActivityTimelineCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No community conversation in this period"
      {...cardState(status, hasData, onRetry)}
      title="Community activity timeline"
      helpText="Weekly post and reply counts across monitored communities, showing changes in conversation activity."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <AreaChart
            data={view.rows}
            onViewDetails={onViewDetails}
            xKey={view.xKey}
            series={view.series}
            /*
             * Unstacked. Stacking would put comments on top of posts and make the
             * comments band's height the *sum* — so reading the figure the KPI
             * above reports would mean subtracting one band from the other.
             */
            stack="none"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
